import dayjs from "dayjs"
import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import { GetEnrolmentsQuery } from "@/api/hasura/generated/graphql"
import {
  EnrolmentBrokerType,
  EnrolmentStatusType,
  IEnrolment,
} from "@/models/operations/enrolment/enrolment.types"
import { Enrolment } from "@/models/operations/enrolment/enrolment"
import {
  ENROLMENT_STATUS_COMPLETED,
  EnrolmentStatus,
} from "@/constants/enrolment"
import { AddressParser } from "@/models/address/address.parser"
import { safeNumber } from "@/helpers/objectHelpers"

export type IEnrolmentRaw = GetEnrolmentsQuery["enrolment"][0]

const getEstimatedUsage = (usageString: string, period: string) => {
  const usage = safeNumber(usageString)
  if (usage) {
    if (period === "Year") return usage
    if (period === "Month") return usage * 12
  }

  return null
}

const getProductType = ({ product, type }) =>
  (type ?? product.type).toLowerCase()

const getMPXN = (products, fuelType) => {
  const agreementMatchingFuel = products.find(
    (productData) => getProductType(productData) === fuelType,
  )
  return agreementMatchingFuel?.meters?.[0]?.identifier ?? null // Despite using an array, enrolments have 1 MPXN per product
}

const getPaymentDetails = (paymentMethod) => ({
  accountName: paymentMethod.bankAccountName,
  paymentAmount: paymentMethod.paymentAmount,
  paymentDayOfMonth: paymentMethod.paymentDayOfMonth,
  seasonalPayment: paymentMethod.seasonalPayment,
  accountNumber: "*****" + paymentMethod.bankAccountNumber.slice(-3),
  sortCode: paymentMethod.bankAccountSortCode.slice(0, 2) + "-**-**",
})
export const EnrolmentParser: IParser<IEnrolment, IEnrolmentRaw> = {
  toDomain: (rawEnrolment) => {
    try {
      const userRequestData = rawEnrolment.data
      const contactData = userRequestData.contacts?.[0]
      const originalContact = contactData
      const contactAddress = contactData?.contactAddress ?? contactData?.address
      const account = rawEnrolment.customer?.billing_accounts?.[0]

      const agreements = userRequestData.products.map(
        ({ code, product, type, meters, consumptionEstimation }) => ({
          productCode: code ?? product.code,
          meterpointIdentifier: meters[0].identifier,
          isElectricity: getProductType({ product, type }) === "electricity",
          estimatedAnnualUsage: getEstimatedUsage(
            consumptionEstimation?.consumption,
            consumptionEstimation?.period,
          ),
        }),
      )

      let status =
        rawEnrolment.status === ENROLMENT_STATUS_COMPLETED
          ? EnrolmentStatus.DONE
          : rawEnrolment.status

      if (account?.terminated) status = EnrolmentStatus.ACCOUNT_TERMINATED
      if (account?.cancelled) status = EnrolmentStatus.ACCOUNT_CANCELLED
      if (account?.closed) status = EnrolmentStatus.ACCOUNT_CLOSED

      return new Enrolment(
        rawEnrolment.id,
        rawEnrolment.broker as EnrolmentBrokerType,
        status as EnrolmentStatusType,
        rawEnrolment.email || originalContact?.email,
        rawEnrolment.post_code ?? "",
        account?.id ?? null,
        account?.number ?? null,
        rawEnrolment.customer?.number ?? null,
        rawEnrolment.enrolment_failure_message ?? null,
        rawEnrolment.third_party_switch_reference ?? null,
        dayjs(rawEnrolment.created_at),
        getMPXN(userRequestData.products, "electricity"),
        getMPXN(userRequestData.products, "gas"),
        AddressParser.toDomain(userRequestData.supplyAddress),
        contactAddress ? AddressParser.toDomain(contactAddress) : null,
        userRequestData.voucher ?? null,
        userRequestData.marketingOptedIn ??
          userRequestData.customerEnrolment.marketingOptedIn,
        userRequestData.extraAssistance ??
          userRequestData.customerEnrolment.extraAssistance,
        agreements,
        {
          firstName:
            originalContact?.firstName ??
            rawEnrolment.customer?.first_name ??
            "",
          lastName:
            originalContact?.lastName ?? rawEnrolment.customer?.last_name ?? "",
          dateOfBirth: originalContact?.birthdate ?? "",
          phoneNumber: originalContact?.phoneNumber ?? "",
        },
        getPaymentDetails(userRequestData.paymentMethod),
        !!rawEnrolment.communications_sent,
        rawEnrolment.retry_count ?? 0,
      )
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
